import React, { useEffect, useRef, useState } from 'react';
import { MdInfoOutline, MdArrowBack, MdArrowForward } from 'react-icons/md';
import { useFormContext } from '../../context/FormContext';
import { useReadOnly } from '../../hooks/useReadOnly';
import { SignupLayout } from '../ui/SignupLayout';
import { Spinner } from 'reactstrap';
import Session from '../auth/Session';
import { updateQuote } from '../services/QuoteService';
import { authFetch, SessionExpiredError } from '../services/apiClient';

// Uncomment for PROD
// const DOCUSIGN_JS_URL = 'https://js.docusign.com/bundle.js';

// Uncomment for Dev
const DOCUSIGN_JS_URL = 'https://js-d.docusign.com/bundle.js';

export function SignApplication({ history }) {
    const { setStep, formData, setField } = useFormContext();
    const { isReadOnly } = useReadOnly();

    const [status, setStatus] = useState('loading'); // loading | signing | error
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        setStep('sign');

        if (isReadOnly) {
            setStatus('signing'); // show container for read-only nav
            return;
        }

        initDocuSign();

        return () => {
            const existing = document.querySelector(`script[src="${DOCUSIGN_JS_URL}"]`);
            if (existing) existing.remove();
            delete window.DocuSign;
        };
    }, []);

    // Initialize DocuSign signing session
    const initDocuSign = async () => {
        setStatus('loading');
        try {
            const returnUrl = `${window.location.origin}/signup/sign`;

            const response = await authFetch('api/DocuSign/CreateSigningSession', {
                method: 'POST',
                body: JSON.stringify({
                    QuoteId: formData.QuoteId,
                    ReturnUrl: returnUrl,
                }),
            });

            if (!response.ok) {
                setErrorMessage('Failed to load the signing session. Please try again.');
                setStatus('error');
                return;
            }

            const result = await response.json();

            if (!result.success || !result.signingUrl) {
                setErrorMessage(result.errorMessage || 'Failed to load signing session.');
                setStatus('error');
                return;
            }

            // Store envelopeId in context for download step
            setField('EnvelopeId', result.envelopeId);

            // Step 2: Load DocuSign JS library
            await loadDocuSignScript();

            // Step 3: Mount Focused View
            const docuSign = await window.DocuSign.loadDocuSign(
                process.env.REACT_APP_DOCUSIGN_INTEGRATION_KEY
            );

            const signing = docuSign.signing({
                url: result.signingUrl,
                displayFormat: 'focused',
                style: {
                    branding: {
                        primaryButton: {
                            backgroundColor: '#D27D28',
                            color: '#fff',
                        },
                    },
                    iframe: {
                        minHeight: '100%',
                        width: '100%',
                    },
                },
            });

            signing.on('ready', () => {
                setStatus('signing');
            });

            signing.on('sessionEnd', async (event) => {
                console.log('DocuSign sessionEnd event:', JSON.stringify(event));

                // DocuSign appends the event to the returnUrl as a query param
                // Parse it from the returnUrl in the event object
                let eventType = event.sessionEndType;

                if (eventType === 'docusign_callback' && event.returnUrl) {
                    const urlParams = new URLSearchParams(new URL(event.returnUrl).search);
                    // Get the last 'event' param since DocuSign appends it twice
                    const allEvents = urlParams.getAll('event');
                    eventType = allEvents[allEvents.length - 1]; // gets 'signing_complete'
                }

                console.log('Resolved event type:', eventType);

                if (eventType === 'signing_complete') {
                    await handleSigningComplete(result.envelopeId);
                } else if (eventType === 'decline') {
                    setErrorMessage('You declined to sign the application. Please go back and contact us if you have questions.');
                    setStatus('error');
                } else if (eventType === 'cancel' || eventType === 'ttl_expired') {
                    initDocuSign();
                } else {
                    setErrorMessage('The signing session ended unexpectedly. Please try again.');
                    setStatus('error');
                }
            });

            signing.mount('#docusign-container');
        } catch (err) {
            if (err instanceof SessionExpiredError) return;
            console.error('DocuSign init error:', err);
            setErrorMessage('Failed to load the signing session. Please try again.');
            setStatus('error');
        }
    };

    // Load DocuSign JS script
    const loadDocuSignScript = () => {
        return new Promise((resolve, reject) => {
            if (window.DocuSign) { resolve(); return; }
            const script = document.createElement('script');
            script.src = DOCUSIGN_JS_URL;
            script.onload = resolve;
            script.onerror = () => reject(new Error('Failed to load DocuSign JS'));
            document.head.appendChild(script);
        });
    };

    // Handle successful signing
    const handleSigningComplete = async (completedEnvelopeId) => {
        setStatus('loading');
        try {
            await authFetch('api/DocuSign/DownloadSignedDocument', {
                method: 'POST',
                body: JSON.stringify({
                    QuoteId: formData.QuoteId,
                    EnvelopeId: completedEnvelopeId,
                }),
            });

            if (formData.QuoteId) {
                try {
                    // Set step to payment after signing - so if a user uses the continue quote feature, we don't create a new envelope and have them sign again.
                    // Only need to sign if they go back to previous steps.
                    await updateQuote(formData.QuoteId, { ...formData, currentStep: 'payment' });
                } catch (err) {
                    if (err instanceof SessionExpiredError) return;
                    console.error('Failed to update quote step:', err);
                }
            }
        } catch (err) {
            if (err instanceof SessionExpiredError) return;
            console.error('Error saving signed document:', err);
        }

        history.push('/signup/payment');
    };

    // Navigation
    const handleBack = () => history.push('/signup/finalize');

    const handleContinue = async () => {
        if (isReadOnly) {
            history.push('/signup/payment');
            return;
        }
        if (formData.QuoteId) {
            try { await updateQuote(formData.QuoteId, formData); }
            catch (err) {
                if (err instanceof SessionExpiredError) return; 
                console.error('Failed to update quote:', err); 
            }
        }
        history.push('/signup/payment');
    };

    return (
        <SignupLayout currentStep={3} totalSteps={3} stepLabel="Sign & Submit">

            <h1 className="signup-heading" style={{ marginTop: 'var(--space-md)' }}>
                Sign your application
            </h1>
            <p className="signup-section__desc">
                Review your application and sign below to submit.
            </p>

            {/* Loading */}
            {status === 'loading' && (
                <>
                    <div className="dp-loading" aria-label="Loading application…">
                        <Spinner />
                    </div>
                    <div className="security-banner" style={{ borderColor: 'var(--brand-primary)' }}>
                        <MdInfoOutline size={20} className="security-banner__icon" />
                        <div className="security-banner__text">
                            <strong>Please keep this window open</strong>
                            <p>
                                We're loading your application - this may take up to{' '}
                                <strong>30 seconds.</strong>
                            </p>
                        </div>
                    </div>
                </>
            )}

            {/* Error */}
            {status === 'error' && (
                <div className="dp-error">
                    <p className="dp-error__message">{errorMessage}</p>
                    <p className="dp-error__help">
                        Need help? Call us at <a href="tel:8772695422">877-269-5422</a>.
                    </p>
                    <div className="signup-nav" style={{ marginTop: 'var(--space-lg)' }}>
                        <button className="btn signup-cta btn--inverse-cta" onClick={handleBack}>
                            <MdArrowBack /> Back
                        </button>
                        <button className="btn signup-cta" onClick={initDocuSign}>
                            Try again
                        </button>
                    </div>
                </div>
            )}

            {/* DocuSign Focused View container - always in DOM, shown when signing */}
            <div
                id="docusign-container"
                style={{
                    width: '100%',
                    height: !isReadOnly ? '700px' : '0',
                    display: status === 'signing' ? 'block' : 'none',
                }}
            />

            {/* Read-only nav for CSR */}
            {isReadOnly && (
                <div className="signup-nav">
                    <button className="btn signup-cta btn--inverse-cta" onClick={handleBack}>
                        <MdArrowBack /> Back
                    </button>
                    <button className="btn signup-cta" onClick={handleContinue}>
                        Continue <MdArrowForward />
                    </button>
                </div>
            )}
            {!isReadOnly && status === "signing" && (
                <div className="signup-nav">
                    <button className="btn signup-cta btn--inverse-cta" onClick={handleBack} style={{maxWidth: "50%"}}>
                        <MdArrowBack /> Back
                    </button>
                    {/* <button className="btn signup-cta" onClick={() => {}} disabled>
                        Continue <MdArrowForward />
                    </button> */}
                </div>
            )}
        </SignupLayout>
    );
}